import React, { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import { Button, Text } from '@gumtree/ui-library';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { trackGA4Event } from '@gumtree/shared/src/util/ga4-shared';
import {
    StyledTotal,
    StyledWrapper,
    StyledIcon,
    StyledInteractiveBox,
    StyledStarsWrapper,
    StyledAverageRating,
    StyledLink,
    submitButtonCss,
} from './style';
import { StarFill, StarSize, RatingText, StarColour } from './constants';
import { generateStars, getStarFill } from './utils';
import GranularFeedback from './granular-feedback';
import { MessageCentreState, MessageCentreUseDispatch } from '../../../../reducers';
import {
    fetchCanUserReview,
    onGranularResponseClear,
    postReview,
} from '../../../../reducers/actions';
import { selectActiveConversation } from '../../../../reducers/selectors';

const Star = ({
    starFill,
    onClick = () => {},
    size = 'small',
    colour = StarColour.yellow,
}: {
    starFill: StarFill;
    onClick?: (e: React.MouseEvent) => void;
    size?: keyof typeof StarSize;
    colour?: keyof typeof StarColour;
}) => (
    <StyledIcon
        size={size}
        type={starFill}
        color={colour}
        className="rating-stars-icon"
        onClick={onClick}
        data-testid={starFill}
    />
);

export const genStaticStars = (rating: number, colour?: keyof typeof StarColour) =>
    generateStars((i) => {
        const starNumber = i + 1;
        const starFill = getStarFill(rating, starNumber);

        return <Star starFill={starFill} key={starNumber} colour={colour} />;
    });

const getInteractiveStars = ({
    interactedRating,
    setInteractedRating,
    selectedInteractedRating,
    setSelectedInteractedRating,
    dispatch,
}: {
    interactedRating: number;
    setInteractedRating: React.Dispatch<React.SetStateAction<number>>;
    selectedInteractedRating: number;
    setSelectedInteractedRating: React.Dispatch<React.SetStateAction<number>>;
    dispatch: MessageCentreUseDispatch;
}) =>
    generateStars((i) => {
        const starNumber = i + 1;
        const starFill = getStarFill(interactedRating, starNumber);

        const handleMouseEnter = () => {
            setInteractedRating(starNumber);
        };

        const handleMouseLeave = () => {
            const selected = selectedInteractedRating || 0;

            setInteractedRating(selected);
        };

        const handleStarClick = (e) => {
            e.stopPropagation();

            trackGA4Event<GA4.ConversationRateUserProgression>({
                event: 'rate_a_user_form_progression',
                formValidation: 'success',
                rateAUserStars: starNumber,
            });
            setSelectedInteractedRating(starNumber);
            setInteractedRating(starNumber);
            dispatch(onGranularResponseClear());
        };

        return (
            <button
                data-q="star-button"
                className="star-button"
                type="button"
                tabIndex={0}
                key={starNumber}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <Star size="large" starFill={starFill} key={starNumber} onClick={handleStarClick} />
            </button>
        );
    });

const RatingStars: FC<RatingStarsProps> = ({
    interactive = false,
    isMobile = false,
    converseeName = '',
}) => {
    const theme = useTheme();
    const [interactedRating, setInteractedRating] = useState<number>(0);
    const [selectedInteractedRating, setSelectedInteractedRating] = useState<number>(0);
    const dispatch = useDispatch() as MessageCentreUseDispatch;
    const { reviews } = useSelector(selectActiveConversation);
    const { canReview } = useSelector(selectState);
    const activeConversation = useSelector(selectActiveConversation);

    const checkCanReviewAndPostReview = async (selectedInteractedRating: number) => {
        dispatch(fetchCanUserReview());
        if (canReview) {
            dispatch(postReview(selectedInteractedRating));
        }
    };

    return (
        <StyledWrapper isMobile={isMobile}>
            {interactive ? (
                <StyledInteractiveBox>
                    <StyledStarsWrapper data-q="star-button">
                        {getInteractiveStars({
                            interactedRating,
                            setInteractedRating,
                            selectedInteractedRating,
                            setSelectedInteractedRating,
                            dispatch,
                        })}
                    </StyledStarsWrapper>
                    {!!interactedRating && (
                        <Text fontLevel="medium" align="center">
                            {' '}
                            It was {RatingText[interactedRating - 1]}
                        </Text>
                    )}
                    {selectedInteractedRating === 1 || selectedInteractedRating === 5 ? (
                        <GranularFeedback
                            selectedInteractedRating={selectedInteractedRating}
                            converseeName={converseeName}
                        />
                    ) : null}
                    {!!selectedInteractedRating && (
                        <Button
                            onClick={() => {
                                checkCanReviewAndPostReview(selectedInteractedRating);
                            }}
                            label="Submit"
                            data-q="rating-submit"
                            css={submitButtonCss(theme)}
                        />
                    )}
                </StyledInteractiveBox>
            ) : (
                <>
                    {reviews ? (
                        <>
                            <StyledLink
                                onClick={() => trackV2('ViewProfileBegin')}
                                href={`/profile/account/${activeConversation.publicProfileId}?review=true`}
                            >
                                <StyledAverageRating data-testid="average-rating">
                                    {Number.isInteger(reviews?.average_rating)
                                        ? reviews?.average_rating
                                        : reviews?.average_rating!.toFixed(1)}
                                </StyledAverageRating>
                                <Star
                                    starFill={StarFill.Full}
                                    colour={StarColour.blade}
                                    size="smaller"
                                />
                            </StyledLink>
                            <StyledTotal isMobile={isMobile}>
                                ({reviews?.total_received})
                            </StyledTotal>
                        </>
                    ) : null}
                </>
            )}
        </StyledWrapper>
    );
};

interface RatingStarsProps {
    converseeName: string;
    interactive?: boolean;
    isMobile?: boolean;
}

export default RatingStars;

function selectState({ messageCentre: { canReview } }: MessageCentreState) {
    return {
        canReview,
    };
}
